import {buildPortalUrlWithCompaniesSync} from 'src/lib/portal-companies-sync'
import {readSelectedCompanies} from 'src/lib/selected-companies-storage'
import storage from 'src/services/storage'
import {isEventsRoutePath, normalizeEventsSearchParams} from './events-search-params-sync'
import {getNewPortalBaseUrl} from './get-new-portal-base-url'
import {OPEN_CHANGE_PASSWORD_SEARCH_PARAM} from './open-change-password-search-param'
import {resolveNewPortalPath} from './resolve-new-portal-path'
import type {SessionBridgeResult} from './session-bridge-result'

type SubmitSessionBridgeInput = {
  legacyPath: string
  search?: string

  forceRedirect?: boolean
}

function appendSearch(path: string, search?: string) {
  if (!search) return path
  return `${path}${search.startsWith('?') ? search : `?${search}`}`
}

function buildBridgeFormFields(redirectTo: string) {
  const accessToken = storage.get('token')
  const refreshToken = storage.get('refreshToken')
  const keepLoggedIn = storage.get('keepLoggedIn') === 'true'

  if (!accessToken || !refreshToken) return null

  return {
    accessToken,
    refreshToken,
    keepLoggedIn: String(keepLoggedIn),
    redirectTo,
  }
}

function submitBridgeForm(action: string, fields: Record<string, string>) {
  const form = document.createElement('form')
  form.method = 'POST'
  form.action = action
  form.style.display = 'none'

  Object.entries(fields).forEach(([name, value]) => {
    const input = document.createElement('input')
    input.type = 'hidden'
    input.name = name
    input.value = value
    form.appendChild(input)
  })

  document.body.appendChild(form)
  form.submit()
}

function buildRedirectToWithCompaniesSync(baseUrl: string, pathWithSearch: string) {
  const syncedUrl = buildPortalUrlWithCompaniesSync(`${baseUrl}${pathWithSearch}`, readSelectedCompanies())
  const {pathname, search} = new URL(syncedUrl)
  return `${pathname}${search}`
}

function preserveBridgeSearchParams(originalSearch: string | undefined, normalizedSearch: string | undefined) {
  if (!normalizedSearch) return normalizedSearch
  if (!originalSearch) return normalizedSearch

  const originalParams = new URLSearchParams(originalSearch.startsWith('?') ? originalSearch.slice(1) : originalSearch)
  const bridgeValue = originalParams.get(OPEN_CHANGE_PASSWORD_SEARCH_PARAM)
  if (!bridgeValue) return normalizedSearch

  const normalizedParams = new URLSearchParams(normalizedSearch.startsWith('?') ? normalizedSearch.slice(1) : normalizedSearch)
  normalizedParams.set(OPEN_CHANGE_PASSWORD_SEARCH_PARAM, bridgeValue)

  const queryString = normalizedParams.toString()
  return queryString ? `?${queryString}` : ''
}

export function submitSessionBridgeToNewPortal({legacyPath, search, forceRedirect}: SubmitSessionBridgeInput): SessionBridgeResult {
  const baseUrl = getNewPortalBaseUrl()
  if (!baseUrl) {
    return {ok: false, reason: 'missing_new_portal_url'}
  }

  const rawSearch = search ? (search.startsWith('?') ? search : `?${search}`) : undefined
  const normalizedSearch = rawSearch && isEventsRoutePath(legacyPath) ? normalizeEventsSearchParams(rawSearch) : search
  const bridgeSearch = preserveBridgeSearchParams(rawSearch ?? search, normalizedSearch)

  const newPortalPath = appendSearch(resolveNewPortalPath(legacyPath), bridgeSearch)
  const redirectTo = buildRedirectToWithCompaniesSync(baseUrl, newPortalPath)

  if (forceRedirect || !storage.get('token') || !storage.get('refreshToken')) {
    window.location.assign(`${baseUrl}${redirectTo}`)
    return {ok: true}
  }

  const fields = buildBridgeFormFields(redirectTo)
  if (!fields) {
    window.location.assign(`${baseUrl}${redirectTo}`)
    return {ok: true}
  }

  submitBridgeForm(`${baseUrl}/api/auth/bridge`, fields)
  return {ok: true}
}
